import React, { useEffect, useState } from 'react';
import SeoArticle from '../components/SeoArticle';
import RichText from '../components/RichText';
import { BLOG_ARTICLE } from '../seo/seoContent';
import Navbar from '../components/Navbar';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import './BlogPage.css';
import { isInternational } from '../shared/signup/usd';

// Posts written for this visitor first: the international ones (audience) abroad, the others in India; newest first
// within each
const forVisitor = (posts) => {
  const abroad = isInternational();
  return [...posts].sort((a, b) => Number((b.audience === 'international') === abroad) - Number((a.audience === 'international') === abroad));
};

// /blog and /blog/<post> on ownezzy.com. The posts are written in the admin panel (Super Admin -> Blog) and come from
// /api/blog. A post's text is plain: a blank line starts a new paragraph, "## " starts a heading and lines starting
// with "- " make a list.
const LIST_TITLE = 'How to Sell Online & Create Shop Websites | Ownezzy Blog';
const LIST_DESCRIPTION = 'Step-by-step guides for Indian shopkeepers, home businesses, and Instagram sellers: how to create a website for your shop, take UPI orders, and grow sales.';

const dateText = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}
function setPageMeta(title, description, url) {
  document.title = title;
  setMeta('meta[name="description"]', 'content', description);
  setMeta('link[rel="canonical"]', 'href', url);
  setMeta('meta[property="og:url"]', 'content', url);
  setMeta('meta[property="og:title"]', 'content', title);
  setMeta('meta[property="og:description"]', 'content', description);
}

export function paragraphs(text) {
  return String(text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

// One block of a post: a heading, a list or a paragraph. **bold** and [label](url) links work inside.
function Block({ text }) {
  if (text.startsWith('## ')) return <h2>{text.slice(3)}</h2>;
  const lines = text.split('\n').map((line) => line.trim());
  if (lines.every((line) => line.startsWith('- '))) return <ul>{lines.map((line, i) => <li key={i}><RichText text={line.slice(2)} /></li>)}</ul>;
  return <p><RichText text={text} /></p>;
}

function useApi(url) {
  const [state, setState] = useState({ data: null, error: '' });
  useEffect(() => {
    let live = true;
    fetch(url)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(res.status === 404 ? 'This post could not be found.' : data.message || 'The blog could not be loaded.');
        if (live) setState({ data, error: '' });
      })
      .catch((err) => live && setState({ data: null, error: err.message }));
    return () => { live = false; };
  }, [url]);
  return state;
}

function Shell({ children }) {
  return (
    <div className="app-shell">
      <AnnouncementBar />
      <Navbar />
      <main className="bl-page">{children}</main>
      <Footer />
    </div>
  );
}

function PostList() {
  const { data, error } = useApi('/api/blog');
  useEffect(() => {
    setPageMeta(LIST_TITLE, LIST_DESCRIPTION, 'https://ownezzy.com/blog');
    window.scrollTo(0, 0);
  }, []);
  return (
    <Shell>
      <section className="bl-hero">
        <div className="container">
          <span className="bl-kicker">OWNEZZY BLOG</span>
          <h1>Ideas to grow your <span>online store</span></h1>
          <p>Guides and tips for shops and sellers moving online.</p>
        </div>
      </section>
      <section className="container bl-list">
        {error && <p className="bl-empty">{error}</p>}
        {!data && !error && <p className="bl-empty">Loading posts…</p>}
        {data && !data.posts.length && <p className="bl-empty">No posts yet. Check back soon.</p>}
        {data && forVisitor(data.posts).map((post) => (
          <a className="bl-card" key={post.slug} href={`/blog/${post.slug}`}>
            {post.coverImage ? <img src={post.coverImage} alt="" loading="lazy" /> : <span className="bl-card-placeholder" aria-hidden="true"><i className="bi bi-journal-text" /></span>}
            <div>
              <small>{dateText(post.publishedAt)}</small>
              <h2>{post.title}</h2>
              <p>{post.excerpt}</p>
              <b>Read more →</b>
            </div>
          </a>
        ))}
      </section>
      <SeoArticle article={BLOG_ARTICLE} />
    </Shell>
  );
}

function Post({ slug }) {
  const { data, error } = useApi(`/api/blog/${encodeURIComponent(slug)}`);
  const post = data && data.post;
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!post) return undefined;
    const url = `https://ownezzy.com/blog/${post.slug}`;
    setPageMeta(`${post.title} | Ownezzy Blog`, post.excerpt.slice(0, 160), url);
    // BlogPosting structured data, so search engines show the post as an article
    const data = document.createElement('script');
    data.type = 'application/ld+json';
    data.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      url,
      mainEntityOfPage: url,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt || post.publishedAt,
      ...(post.coverImage ? { image: new URL(post.coverImage, 'https://ownezzy.com').href } : {}),
      inLanguage: 'en-IN',
      author: { '@id': 'https://ownezzy.com/#organization' },
      publisher: { '@id': 'https://ownezzy.com/#organization' },
    });
    document.head.appendChild(data);
    return () => data.remove();
  }, [post]);
  return (
    <Shell>
      <article className="container bl-post">
        <nav className="bl-crumbs"><a href="/">Home</a> / <a href="/blog">Blog</a></nav>
        {error && <p className="bl-empty">{error} <a href="/blog">See all posts</a></p>}
        {!post && !error && <p className="bl-empty">Loading…</p>}
        {post && (
          <>
            <h1>{post.title}</h1>
            <small className="bl-date">{dateText(post.publishedAt)}</small>
            {post.coverImage && <img className="bl-cover" src={post.coverImage} alt="" />}
            <div className="bl-body">{paragraphs(post.body).map((p, i) => <Block key={i} text={p} />)}</div>
            <a className="bl-back" href="/blog">← All posts</a>
          </>
        )}
      </article>
    </Shell>
  );
}

export default function BlogPage({ slug }) {
  return slug ? <Post slug={slug} /> : <PostList />;
}
